import { useTranslation } from "next-i18next";
import React, { useState, useEffect } from "react";
import CircleIcon from "@mui/icons-material/Circle";
import DoneIcon from "@mui/icons-material/Done";
import classes from "./PasswordStrengthChecker.module.scss";

type PropTypes = {
  value: {
    password: string;
    confirmedPassword: string;
  };
  style?: any;
  fieldName: string;
};

function PasswordStrengthChecker(props: PropTypes) {
  const { t } = useTranslation("common", {
    keyPrefix: "customer.login.password_rules",
  });
  const {
    value: { password, confirmedPassword },
    fieldName,
  } = props;

  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isLength, setIsLength] = useState(false);
  const [containsUppercase, setContainsUppercase] = useState(false);
  const [containsLowercase, setContainsLowercase] = useState(false);
  const [containsNumber, setContainsNumber] = useState(false);
  const [containsSpecialChar, setContainsSpecialChar] = useState(false);
  const [isPasswordMatch, setIsPasswordMatch] = useState(false);

  /* The `PASSWORD_RULES` constant is an array of objects that define the rules for password strength.
  Each object in the array represents a specific rule and has three properties: */
  const PASSWORD_RULES = [
    {
      id: 1,
      rule: t("min_max_length"),
      value: isLength,
    },
    {
      id: 2,
      rule: t("uppercase"),
      value: containsUppercase,
    },
    {
      id: 3,
      rule: t("lowercase"),
      value: containsLowercase,
    },
    {
      id: 4,
      rule: t("number"),
      value: containsNumber,
    },
    {
      id: 5,
      rule: t("special_char"),
      value: containsSpecialChar,
    },
  ];

  useEffect(() => {
    if (fieldName === "passwordConfirm") {
      if (password?.length > 0 && confirmedPassword?.length > 0) {
        setIsPasswordMatch(password === confirmedPassword);
      } else {
        setIsPasswordMatch(false);
      }
    } else {
      setIsLength(password?.length >= 8 && password?.length <= 25);
      setContainsUppercase(/[A-Z]/.test(password));
      setContainsLowercase(/[a-z]/.test(password));
      setContainsNumber(/\d/.test(password));
      setContainsSpecialChar(
        /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
      );
      if (
        isLength &&
        containsUppercase &&
        containsLowercase &&
        containsNumber &&
        containsSpecialChar
      ) {
        setIsPasswordValid(true);
      } else {
        setIsPasswordValid(false);
      }
    }
  }, [
    fieldName,
    password,
    confirmedPassword,
    isLength,
    containsUppercase,
    containsLowercase,
    containsNumber,
    containsSpecialChar,
  ]);

  /* The `renderIcon` function is a helper function that takes a boolean `condition` as a parameter. It
  returns an icon component based on the value of the `condition`. */
  const renderIcon = (condition: boolean) => {
    return condition ? (
      <DoneIcon color="success" fontSize="small" />
    ) : (
      <CircleIcon className={`${classes.icon_size}`} color="error" />
    );
  };

  const passwordRuleRender = () => (
    <div className={`${classes.pwd_container}`}>
      {t("heading")}
      {PASSWORD_RULES.map((ele) => (
        <div key={ele.id} className={`${classes.pwd_container_container} `}>
          <div className={`${classes.pwd_icon_container}`}>
            {renderIcon(ele.value)}
          </div>
          <div
            className={`${classes.pwd_container_item} ${
              classes[ele.value ? "pwd_success" : "pwd_failed"]
            }`}
          >
            {ele.rule}
          </div>
        </div>
      ))}
    </div>
  );

  const passwordMatcherRender = () => (
    <div className={`${classes.pwd_container}`}>
      {t("pwd_match_heading")}
      <div key={1} className={`${classes.pwd_container_container} `}>
        <div className={`${classes.pwd_icon_container}`}>
          {renderIcon(isPasswordMatch)}
        </div>
        <div
          className={`${classes.pwd_container_item} ${
            classes[isPasswordMatch ? "pwd_success" : "pwd_failed"]
          }`}
        >
          {isPasswordMatch ? t("pwd_match") : t("pwd_dont_match")}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {fieldName === "password" && !isPasswordValid
        ? passwordRuleRender()
        : fieldName === "passwordConfirm"
        ? passwordMatcherRender()
        : ""}
    </>
  );
}

export default PasswordStrengthChecker;
